import type { RouterConfig } from '@nuxt/schema';

import { createMemoryHistory, createWebHistory } from 'vue-router';

import { EMPTY_CLUSTER, parseClusterDefinitions, resolveClusterFromHeader } from '~/utils/clusters';

const STATE_KEY = 'cluster';

/**
 * Cluster-aware Vue Router base.
 *
 * Caddy strips the cluster path prefix from the URL before forwarding to
 * Nuxt and injects an `X-Cluster-Code` header. We resolve the cluster on
 * SSR, persist it to shared Nuxt state (which serializes to the hydration
 * payload), and feed the path back into Vue Router as its `base`:
 *   - SSR: request URL is already stripped (`/learn`); base ensures
 *     generated URLs include the prefix (`<a href="/au/profile">`).
 *   - Client: browser URL is `/au/learn`; router strips base on parse so
 *     `/learn` matches the bare route. Resolution comes free via the
 *     hydrated `useState('cluster')` — no client-side env lookup needed.
 *
 * Resolution lives in this factory (not a plugin) because it runs at app
 * creation — before anything reads `useState('cluster')` via useCurrentCluster —
 * so it's the earliest hook that can both populate the state and set the base.
 */
export default {
  history: (defaultBase) => {
    const clusterState = useState(STATE_KEY, () => EMPTY_CLUSTER);

    if (import.meta.server) {
      const code = useRequestHeaders(['x-cluster-code'])['x-cluster-code'] ?? null;

      // Only parseClusterDefinitions can throw — a malformed CLUSTER_DEFINITIONS is a
      // deploy misconfig. Surface it loudly and degrade to the root cluster (empty
      // definitions → EMPTY_CLUSTER) rather than failing router creation. Failures in
      // useState/useRequestHeaders are real bugs and intentionally propagate.
      let definitions: ReturnType<typeof parseClusterDefinitions> = [];
      try {
        definitions = parseClusterDefinitions(useRuntimeConfig().clusterDefinitions);
      }
      catch (err) {
        console.error('[router.options] invalid CLUSTER_DEFINITIONS', err);
      }

      clusterState.value = resolveClusterFromHeader(definitions, code);
    }

    const base = clusterState.value.pathPrefix || defaultBase || '/';
    return import.meta.client ? createWebHistory(base) : createMemoryHistory(base);
  },
} satisfies RouterConfig;
